import PropTypes from 'prop-types'
import React from 'react'
import { Col } from 'react-bootstrap'

import { useQuery } from '@tanstack/react-query'
import { useSchNetApiClient } from '../SchNetApi'
import useSiteAreaOrganisations from '../hooks/useSiteAreaOrganisations'

import ErrorPage from './ErrorPage'
import ProgramCard from './ProgramCard'
import { isOrganisationPrioritized } from '../utils'

function compareByAbsolutePrice(left, right) {
  return parseFloat(left.absolute_price) - parseFloat(right.absolute_price)
}

function compareByDuration(left, right) {
  return left.duration - right.duration
}

function compareByCountInPeriod(left, right) {
  return left.rating.count_in_period - right.rating.count_in_period
}

function ProgramList ({ sortOrder, filter, limit, showInfo, ...props }) {
  const schNetApiClient = useSchNetApiClient()
  const rowClasses = props.narrow ? 'row-cols-1 row-cols-lg-2' : 'row-cols-1 row-cols-md-2 row-cols-xl-3'

  // reduce list of programs to uniq organisations,
  // picking longest duration/cheapest price
   const condensedPrograms = (programs) => {
      let groupedByOrga = {}
      let cPrograms = []

      programs.forEach((program) => {
        let slug = program.organisation.slug

        groupedByOrga[slug] = groupedByOrga[slug] || []
        groupedByOrga[slug].push(program)
      })
      Object.keys(groupedByOrga).forEach((slug) => {
        let orgaPrograms = groupedByOrga[slug]

        if (orgaPrograms.length) {
          let longestDuration
          let longestPrograms
          let cheapestProgram

          orgaPrograms = orgaPrograms.sort(compareByDuration).reverse()
          longestDuration = orgaPrograms[0].duration
          longestPrograms = orgaPrograms.filter((program) => {
            return longestDuration === program.duration
          })
          cheapestProgram = longestPrograms.sort(compareByAbsolutePrice)[0]
          cPrograms.push(cheapestProgram)
        }
      })

      return cPrograms
   }

  // map organisation from CRM (dropping programs for which no organisation from crm can be found),
  // find and parition by priority placement
  //
  // returns
  //  - the resulting list of displayablePrograms (possibly without priority program)
  //  - possibly a priority program
  const mapPrograms = (programs, organisations) => {
    return programs.reduce(({ displayablePrograms, priorityProgram }, program) => {
      let organisation = organisations.find(orga => program.organisation.slug === orga.slug)

      if (!organisation) {
        // early return without this program, as we cannot find corresponding
        // organisation (this is a content issue, as the given organisation has to be
        // linked to this site within the crm)
        // TODO: maybe somehow report this problem
        return {
          displayablePrograms,
          priorityProgram,
        }
      }

      // TODO: once we have imgproxy-based logo-urls from CRM
      // we can remove this override. Until then we need s.net's
      // paperclip'ed logo urls
      organisation.logo_url = program.organisation.logo_url

      program.organisation = organisation

      // in utils see hint on start-date/end-date -
      let priorityPlacement = isOrganisationPrioritized({
        organisation,
        filterContext: 'programme',
        filterCountryCode: filter.countryCode,
      })

      if (priorityPlacement) {
        priorityProgram = program
      } else {
        displayablePrograms.push(program)
      }

      return {
        displayablePrograms,
        priorityProgram,
      }
    }, {
      displayablePrograms: [],
      priorityProgram: null,
    })
  }

  const sortedPrograms = (programs) => {
    // copy input first to avoid mutation
    let newPrograms = [...programs]

    switch (sortOrder) {
      case 'popularity':
        // NOTE: we condense the list in popular order
        return condensedPrograms(newPrograms).sort(compareByCountInPeriod).reverse()
      case 'price_ascending':
        return newPrograms.sort(compareByAbsolutePrice)
      case 'price_descending':
        return newPrograms.sort(compareByAbsolutePrice).reverse()
      default:
        return newPrograms
    }
  }

  const filteredPrograms = (programs, params) => {
    return programs.filter((program) => {
      const matchesDuration = params.duration === ''
                         || (parseInt(params.duration, 10) === 0 && program.duration < 8) || program.duration === parseInt(params.duration, 10)
      const matchesYear = !params.startYear
                     || (new RegExp('^' + params.startYear)).test(program.start_date)
      const matchesPrice = !params.price
                        || parseFloat(params.price) >= parseFloat(program.absolute_price)
      const matchesOrganisationId = !params.organisationId
                           || (program.organisation.slug === params.organisationId)
      // NOTE: filter flags are ANDed therefore `matchesFlag` will be `true`
      // only if program meets ALL filter flags
      //
      // if the a given filter flag is not set (checkbox unchecked),
      // this check always returns `true` for the given flag,
      // i.e. a program always matches (therefore `matchesFlag` is always true
      // for every program if there are no filter flags set)!
      //
      // see: https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Array/every
      const matchesFlag = Object.keys(params.flags).every((flag) => {
        const flagValue = params.flags[flag]

        return !flagValue || program.flags[flag]
      })
      return matchesOrganisationId && matchesDuration && matchesYear && matchesPrice && matchesFlag
    })
  }

  const {
    data: programs,
    isError: programsIsError,
    isLoading: programsIsLoading,
    error: programsError,
  } = useQuery(['programs', filter.countryCode, filter.organisationId], () => {
    return schNetApiClient.fetchPrograms(filter.countryCode, filter.organisationId)
  })

  const {
    data: organisations,
    isError: organisationsIsError,
    isLoading: organisationsIsLoading,
    error: organisationsError
  } = useSiteAreaOrganisations('schueleraustausch')

  if (programsIsLoading || organisationsIsLoading) {
    return (
      <div className="d-flex flex-column align-items-center" data-testid='loading-indicator'>
        <p>Loading...</p>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (programsIsError || organisationsIsError) {
    return (
      <ErrorPage
        message={programsError ?
                 programsError.message :
                 organisationsError.message}
      />
    )
  }

  const fPrograms = filteredPrograms(programs, filter)
  const sPrograms = sortedPrograms(fPrograms)
  const { displayablePrograms, priorityProgram } = mapPrograms(sPrograms, organisations)

  // by default no-limit on programs
  let limitedPrograms = displayablePrograms
  let programCountInfo = (
    <p>
      {displayablePrograms.length} Programme gefunden
    </p>
  )

  if (limit > 0) {
    // deduce one program if we have a priority placement
    const limitWithoutPriority = priorityProgram ? (limit - 1) : limit
    programCountInfo = (
      <p>
        Die ersten {limit} von {displayablePrograms.length} gefundenen Programmen
      </p>
    )
    limitedPrograms = displayablePrograms.slice(0, limitWithoutPriority)
  }

  const programCards = limitedPrograms.map((program, index) => {
    return (
      <Col key={index}>
        <ProgramCard
          ratingsLinkPrefix={props.ratingsLinkPrefix}
          onOrderClicked={props.onOrderClickedForOrga}
          {...program}
        />
      </Col>
    )
  })

  return (
    <React.Fragment>
      {showInfo ? programCountInfo : null}
      <div data-testid='program-list' className={`program-list row ${rowClasses} g-4`}>
        {priorityProgram && (
          <Col>
            <ProgramCard
              {...priorityProgram}
              variant='priority'
              ratingsLinkPrefix={props.ratingsLinkPrefix}
              onOrderClicked={props.onOrderClickedForOrga}
            />
          </Col>
        )}
        {programCards}
      </div>
    </React.Fragment>
  )
}

ProgramList.propTypes = {
  onOrderClickedForOrga: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  sortOrder: PropTypes.string.isRequired,
  ratingsLinkPrefix: PropTypes.string,
  showInfo: PropTypes.bool,
  limit: PropTypes.number,
}

export default ProgramList
